// uiarc: input/textarea yardım ve hata satırı (https://uiarc.dev/r/input.json `FieldMessage`).
// Satır yüksekliğini yayla açar, sonra sözcükler bulanıklıktan yükselir; yalnız değişen sözcük hareket eder,
// sayılar basamak basamak döner. Ekran okuyucu düz metni okur. `prefers-reduced-motion` altında hareket yok.
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import type { Variants } from 'motion/react'
import { motionTokens } from '@/lib/motion'
import { cn } from '@/lib/utils'

const { blur, duration, ease, spring } = motionTokens

/** Basamaklar sayı büyüyünce yukarı, küçülünce aşağı döner; `custom` çıkmakta olanlara son yönü verir. */
const digit: Variants = {
  enter: (direction: number) => ({
    opacity: 0,
    y: `${direction * 0.6}em`,
    filter: `blur(${blur.subtle}px)`,
  }),
  center: { opacity: 1, y: 0, filter: 'blur(0px)' },
  exit: (direction: number) => ({
    opacity: 0,
    y: `${direction * -0.6}em`,
    filter: `blur(${blur.subtle}px)`,
  }),
}

const isNumber = (word: string) => /^\d[\d.,/:]*%?$/.test(word)
const toNumber = (word: string) => parseFloat(word.replace(/,/g, ''))

/** Metindeki bir sayı ("12 karakter"): yalnız değişen basamaklar döner; kazanılan/kaybolan basamak genişliği açar/kapar. */
function RollingNumber({ word }: { word: string }) {
  const reduced = useReducedMotion()
  const [shown, setShown] = useState({ word, direction: 1 })
  if (shown.word !== word)
    setShown({ word, direction: toNumber(word) < toNumber(shown.word) ? -1 : 1 })
  const characters = word.split('')
  const transition = reduced
    ? { duration: 0 }
    : {
        y: motionTokens.spring.snappy,
        width: motionTokens.spring.morph,
        opacity: { duration: duration.fast },
        filter: { duration: duration.fast },
      }
  return (
    <AnimatePresence initial={false}>
      {characters.map((character, index) => (
        <motion.span
          key={characters.length - index}
          className="relative inline-flex justify-center tabular-nums [&>span]:inline-block"
          initial={{ width: 0, opacity: 0 }}
          animate={{ width: 'auto', opacity: 1 }}
          exit={{ width: 0, opacity: 0 }}
          transition={transition}
        >
          <AnimatePresence initial={false} mode="popLayout" custom={shown.direction}>
            <motion.span
              key={character}
              custom={shown.direction}
              variants={digit}
              initial="enter"
              animate="center"
              exit="exit"
              transition={transition}
            >
              {character}
            </motion.span>
          </AnimatePresence>
        </motion.span>
      ))}
    </AnimatePresence>
  )
}

/** Değişen sözcükler yükselip netleşir, değişmeyenler yerinde kalır, sayılar döner. */
function MotionText({ text }: { text: string }) {
  const reduced = useReducedMotion()
  const words = text.split(' ')
  return (
    <>
      <span className="sr-only">{text}</span>
      <span className="relative block" aria-hidden="true">
        <AnimatePresence initial={false} mode="popLayout">
          {words.map((word, index) => (
            <motion.span
              key={`${index}:${isNumber(word) ? '#' : word}`}
              className="inline-block whitespace-pre"
              initial={
                reduced
                  ? { opacity: 0 }
                  : { opacity: 0, y: '0.35em', filter: `blur(${blur.soft}px)` }
              }
              animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              exit={
                reduced
                  ? { opacity: 0, transition: { duration: 0 } }
                  : {
                      opacity: 0,
                      y: '-0.35em',
                      filter: `blur(${blur.subtle}px)`,
                      transition: { duration: duration.exit, ease: ease.standard },
                    }
              }
              transition={
                reduced
                  ? { duration: duration.instant }
                  : { duration: duration.standard, ease: ease.enter }
              }
            >
              {isNumber(word) ? <RollingNumber word={word} /> : word}
              {index < words.length - 1 ? ' ' : null}
            </motion.span>
          ))}
        </AnimatePresence>
      </span>
    </>
  )
}

/** Satır ölçülen metni izler: uzayıp alta sarılan mesaj bir sonraki satırını açar, sıçramaz. */
function MessageRow({
  id,
  children,
  className,
  alert,
}: {
  id?: string
  children: ReactNode
  className?: string
  alert?: boolean
}) {
  const reduced = useReducedMotion()
  const copyRef = useRef<HTMLSpanElement>(null)
  const [height, setHeight] = useState<number | 'auto'>('auto')
  useEffect(() => {
    const node = copyRef.current
    if (!node || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setHeight(entry.borderBoxSize?.[0]?.blockSize ?? node.offsetHeight)
    })
    observer.observe(node)
    return () => observer.disconnect()
  }, [])
  // Azaltılmış harekette satır tam yükseklikte bağlanır: sıfır süreli açılış bile bir kapalı kare boyardı.
  return (
    <motion.span
      data-slot="field-message-slot"
      className="block overflow-hidden"
      initial={reduced ? false : { height: 0, opacity: 0 }}
      animate={{ height, opacity: 1 }}
      exit={{
        height: 0,
        opacity: 0,
        transition: reduced
          ? { duration: 0 }
          : { height: spring.smooth, opacity: { duration: duration.instant } },
      }}
      transition={
        reduced ? { duration: 0 } : { height: spring.smooth, opacity: { duration: duration.fast } }
      }
    >
      <motion.span
        ref={copyRef}
        id={id}
        className={cn('leading-body block pt-2 text-xs', className)}
        role={alert ? 'alert' : undefined}
        initial={reduced ? false : { y: '0.35em', filter: `blur(${blur.soft}px)` }}
        animate={{ y: 0, filter: 'blur(0px)' }}
        transition={{ duration: reduced ? 0 : duration.standard, ease: ease.enter }}
      >
        {typeof children === 'string' ? <MotionText text={children} /> : children}
      </motion.span>
    </motion.span>
  )
}

/**
 * Yardım/hata satırı: içerik varken satır yayla açılır, yokken kapanır (üst boşluğu satırın kendisi taşır;
 * kapalıyken hiç yer tutmaz). Çağıran kapsayıcı `gap` vermemeli.
 */
function FieldMessage({
  id,
  className,
  alert,
  children,
}: {
  id?: string
  className?: string
  alert?: boolean
  children?: ReactNode
}) {
  return (
    <AnimatePresence initial={false}>
      {children ? (
        <MessageRow key="message" id={id} className={className} alert={alert}>
          {children}
        </MessageRow>
      ) : null}
    </AnimatePresence>
  )
}

export { FieldMessage }
